'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { CustomersSection } from '@/types/sanity';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';

interface CustomersSectionComponentProps {
  section: CustomersSection;
  pageSlug?: string;
}

export function CustomersSectionComponent({ section, pageSlug }: CustomersSectionComponentProps) {
  const industryFilter = pageSlug === 'dental' || pageSlug === 'audio' ? pageSlug : undefined;
  const customers = (section.customers || []).filter(
    (c) => !industryFilter || c.industry === industryFilter
  );
  const customersWithTestimonials = customers.filter((c) => c.testimonial?.quote);

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'customers-section py-8 md:py-12 lg:py-16',
          pageSlug && `customers-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader
              title={section.title}
              subtitle={section.subtitle}
              description={section.description}
            />

            {/* Logos Grid */}
            {customers.length > 0 && (
              <StaggerContainer
                staggerDelay={0.1}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-16 max-w-5xl mx-auto"
              >
                {customers.map((customer) => (
                  <motion.div
                    key={customer._id}
                    variants={staggerItemVariants}
                    className="flex items-center justify-center"
                  >
                    <div className="relative w-full aspect-3/2 rounded-lg bg-white p-4">
                      {customer.logo?.asset ? (
                        <Image
                          src={urlFor(customer.logo).width(240).height(160).quality(85).url()}
                          alt={customer.logo?.alt || customer.name}
                          fill
                          sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 160px"
                          className="object-contain"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-sm font-medium text-muted-foreground">
                          {customer.name}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </StaggerContainer>
            )}

            {/* Testimonial Slider */}
            {customersWithTestimonials.length > 0 && (
              <div className="max-w-4xl mx-auto px-12">
                <Carousel opts={{ loop: true }}>
                  <CarouselContent>
                    {customersWithTestimonials.map((customer) => (
                      <CarouselItem key={customer._id}>
                        <div className="text-center px-4 py-8">
                          <blockquote className="text-lg md:text-xl italic text-foreground/90 mb-6 leading-relaxed">
                            &ldquo;{customer.testimonial!.quote}&rdquo;
                          </blockquote>
                          <div className="flex items-center justify-center gap-3">
                            {customer.testimonial!.avatar?.asset ? (
                              <Avatar>
                                <AvatarImage
                                  src={urlFor(customer.testimonial!.avatar)
                                    .width(96)
                                    .height(96)
                                    .url()}
                                  alt={
                                    customer.testimonial!.avatar?.alt ||
                                    customer.testimonial!.author ||
                                    customer.name
                                  }
                                />
                                <AvatarFallback>
                                  {(customer.testimonial!.author || customer.name).charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <Avatar>
                                <AvatarFallback>
                                  {(customer.testimonial!.author || customer.name).charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <div className="text-left">
                              <p className="font-semibold">
                                {customer.testimonial!.author || customer.name}
                              </p>
                              {customer.testimonial!.position && (
                                <p className="text-sm text-muted-foreground">
                                  {customer.testimonial!.position}
                                </p>
                              )}
                            </div>
                          </div>
                          {customer.logo?.asset && (
                            <div className="mt-4 flex items-center justify-center gap-2">
                              <div className="relative h-6 w-24">
                                <Image
                                  src={urlFor(customer.logo)
                                    .width(120)
                                    .height(30)
                                    .quality(85)
                                    .url()}
                                  alt={customer.logo?.alt || customer.name}
                                  fill
                                  className="object-contain"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {customersWithTestimonials.length > 1 && (
                    <>
                      <CarouselPrevious />
                      <CarouselNext />
                    </>
                  )}
                </Carousel>
              </div>
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
